import { salesStatusRequiresProduct } from '@shared/constants/sales-action-display';
import { isNextActionAllowedForStatus } from '@shared/constants/next-action-matrix';
import type {
  ActionType,
  LostReason,
  SalesAction,
  SalesStatus,
} from '@shared/types/sales-action';

export type SalesActionDraftMode = 'create' | 'edit';

export interface SalesActionDraftValues {
  solutionId: string;
  candidateSolutionIds: string[];
  salesStatus: SalesStatus;
  actionType: ActionType | '';
  actionTitle: string;
  dueDate: string;
  note: string;
  lostReason: LostReason | '';
  lostReasonNote: string;
  sourceCustomerProblemId: string;
  participantLawyerIds: string[];
}

type ExistingSalesActionDraftSource = Pick<
  SalesAction,
  | 'product_id'
  | 'candidate_product_ids'
  | 'sales_status'
  | 'action_type'
  | 'action_title'
  | 'due_date'
  | 'note'
  | 'lost_reason'
  | 'lost_reason_note'
  | 'source_customer_problem_id'
  | 'participant_lawyer_ids'
>;

/**
 * Build the complete form seed without reading reactive component state.
 * Invalid legacy action/status combinations are represented as the empty
 * selection, matching the options the form can actually display.
 *
 * `anchorLawyerId` is the row's own anchor lawyer (the lawyer a
 * `target.kind === 'lawyer'` row is anchored to) — required in participants by
 * the write invariant (86eya0dva). When provided it is force-included in the
 * seed so the form never opens in a state the backend would reject; the
 * checkbox itself is locked (non-removable) by the caller.
 */
export function buildInitialSalesActionDraft(
  existing?: ExistingSalesActionDraftSource,
  anchorLawyerId?: string,
): SalesActionDraftValues {
  const salesStatus = existing?.sales_status ?? 'no_contact';
  const existingActionType = existing?.action_type;
  const participantLawyerIds = [...(existing?.participant_lawyer_ids ?? [])];
  if (anchorLawyerId && !participantLawyerIds.includes(anchorLawyerId)) {
    participantLawyerIds.push(anchorLawyerId);
  }

  return {
    solutionId: existing?.product_id ?? '',
    candidateSolutionIds: [...(existing?.candidate_product_ids ?? [])],
    salesStatus,
    actionType:
      existingActionType &&
      isNextActionAllowedForStatus(salesStatus, existingActionType)
        ? existingActionType
        : '',
    actionTitle: existing?.action_title ?? '',
    dueDate: existing?.due_date ?? '',
    note: existing?.note ?? '',
    lostReason: existing?.lost_reason ?? '',
    lostReasonNote: existing?.lost_reason_note ?? '',
    sourceCustomerProblemId: existing?.source_customer_problem_id ?? '',
    participantLawyerIds,
  };
}

export interface SalesActionDraftSnapshot {
  mode: SalesActionDraftMode;
  solutionId: string;
  candidateSolutionIds: readonly string[];
  salesStatus: SalesStatus;
  actionType: ActionType | '';
  actionTitle: string;
  dueDate: string;
  note: string;
  lostReason: LostReason | '';
  lostReasonNote: string;
  sourceCustomerProblemId: string;
  participantLawyerIds: readonly string[];
}

function sortedSet(values: readonly string[]): string[] {
  return [...new Set(values)].sort();
}

/**
 * Capture only the values which the current form mode can save. This keeps
 * stale values from conditionally hidden inputs from producing false dirty
 * states while preserving all user-visible edits.
 */
export function takeSalesActionDraftSnapshot(
  mode: SalesActionDraftMode,
  values: SalesActionDraftValues,
): SalesActionDraftSnapshot {
  const isCreate = mode === 'create';
  const isDealStatus = salesStatusRequiresProduct(values.salesStatus);
  const isCreatingLost = isCreate && values.salesStatus === 'lost';
  const actionType =
    values.actionType !== '' &&
    isNextActionAllowedForStatus(values.salesStatus, values.actionType)
      ? values.actionType
      : '';

  return {
    mode,
    solutionId: isCreate && isDealStatus ? values.solutionId : '',
    candidateSolutionIds:
      isCreate && !isDealStatus ? sortedSet(values.candidateSolutionIds) : [],
    salesStatus: values.salesStatus,
    actionType,
    actionTitle: values.actionTitle,
    dueDate: values.dueDate,
    note: values.note,
    lostReason: isCreatingLost ? values.lostReason : '',
    lostReasonNote: isCreatingLost ? values.lostReasonNote : '',
    sourceCustomerProblemId: values.sourceCustomerProblemId,
    participantLawyerIds: sortedSet(values.participantLawyerIds),
  };
}

function stringArraysEqual(
  left: readonly string[],
  right: readonly string[],
): boolean {
  return (
    left.length === right.length &&
    left.every((value, index) => value === right[index])
  );
}

function snapshotsEqual(
  left: SalesActionDraftSnapshot,
  right: SalesActionDraftSnapshot,
): boolean {
  return (
    left.mode === right.mode &&
    left.solutionId === right.solutionId &&
    stringArraysEqual(left.candidateSolutionIds, right.candidateSolutionIds) &&
    left.salesStatus === right.salesStatus &&
    left.actionType === right.actionType &&
    left.actionTitle === right.actionTitle &&
    left.dueDate === right.dueDate &&
    left.note === right.note &&
    left.lostReason === right.lostReason &&
    left.lostReasonNote === right.lostReasonNote &&
    left.sourceCustomerProblemId === right.sourceCustomerProblemId &&
    stringArraysEqual(left.participantLawyerIds, right.participantLawyerIds)
  );
}

export function isSalesActionDraftDirty(
  openingSnapshot: SalesActionDraftSnapshot,
  mode: SalesActionDraftMode,
  currentValues: SalesActionDraftValues,
): boolean {
  return !snapshotsEqual(
    openingSnapshot,
    takeSalesActionDraftSnapshot(mode, currentValues),
  );
}
